// hooks/useMemberCard.ts - Version complète avec nouveau formulaire PDF
import { useState, useEffect } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import GTMTrackingService from '../services/gtmTracking';
import { MemberData } from '../types/adhesion.types'; // ← IMPORT DU TYPE OFFICIEL

export const useMemberCard = (memberData: MemberData | null) => {
    // États existants...
    const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
    const [showQRModal, setShowQRModal] = useState(false);
    const [qrType, setQrType] = useState<'simple' | 'compact' | 'optimal'>('simple');
    const [isDownloadingCard, setIsDownloadingCard] = useState(false);
    const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);
    const [downloadError, setDownloadError] = useState<string | null>(null);
    const [pdfError, setPdfError] = useState<string | null>(null);
    const [copySuccess, setCopySuccess] = useState(false);

    // Générer le QR Code quand les données changent
    useEffect(() => {
        if (memberData) {
            generateQRCode();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [memberData, qrType]);

    // ==========================================
    // FONCTIONS QR CODE
    // ==========================================
    const generateQRCode = () => {
        if (!memberData) return;

        try {
            const qrData = `UDB Member: ${memberData.first_name} ${memberData.last_name} - ${memberData.member_number}`;
            const encodedData = encodeURIComponent(qrData);
            const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodedData}&color=000000&bgcolor=FFFFFF&qzone=2&format=png`;
            setQrCodeUrl(qrUrl);
        } catch (error) {
            console.error('Erreur génération QR Code:', error);
        }
    };

    const generatePlainTextMemberQR = (size: string = '400x400'): string => {
        if (!memberData) return '';

        const lines = [
            'UDB',
            '',
            `Numero: ${memberData.member_number}`,
            `Nom: ${memberData.first_name} ${memberData.last_name}`,
            `NIP: ${memberData.nip || 'Non renseigné'}`
        ];

        const memberInfo = lines.join(' | ');
        const encodedData = encodeURIComponent(memberInfo);
        return `https://api.qrserver.com/v1/create-qr-code/?size=${size}&data=${encodedData}&color=000000&bgcolor=FFFFFF&qzone=2&format=png`;
    };

    // ==========================================
    // FONCTIONS UTILITAIRES PDF
    // ==========================================
    const checkLogoAvailability = (): Promise<boolean> => {
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => resolve(true);
            img.onerror = () => resolve(false);
            img.src = '/logo512.png';
        });
    };

    const loadLogoImage = (): Promise<string> => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';

            img.onload = () => {
                try {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');
                    canvas.width = 200;
                    canvas.height = 200;

                    if (ctx) {

                        // Améliorer la qualité de rendu
                        ctx.imageSmoothingEnabled = true;
                        ctx.imageSmoothingQuality = 'high';

                        ctx.fillStyle = 'white';
                        ctx.fillRect(0, 0, 200, 200);
                        ctx.drawImage(img, 0, 0, 200, 200);
                        const logoData = canvas.toDataURL('image/png', 1.0);
                        resolve(logoData);
                    } else {
                        reject(new Error('Impossible de créer le contexte canvas'));
                    }
                } catch (error) {
                    reject(error);
                }
            };

            img.onerror = () => reject(new Error('Impossible de charger le logo'));
            img.src = '/logo128.png';
        });
    };

    // ==========================================
    // FONCTION POUR CHARGER LA SIGNATURE FONDATEUR
    // ==========================================
    const loadFounderSignature = (): Promise<string> => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';

            img.onload = () => {
                try {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');
                    canvas.width = 320; // Largeur recommandée
                    canvas.height = 160; // Hauteur recommandée (ratio 2:1)

                    if (ctx) {
                        // Fond blanc pour la signature
                        ctx.fillStyle = 'white';
                        ctx.fillRect(0, 0, 320, 160);

                        // Dessiner l'image de signature
                        ctx.drawImage(img, 0, 0, 320, 160);
                        const signatureData = canvas.toDataURL('image/png', 1.0);
                        resolve(signatureData);
                    } else {
                        reject(new Error('Impossible de créer le contexte canvas'));
                    }
                } catch (error) {
                    reject(error);
                }
            };

            img.onerror = () => reject(new Error('Impossible de charger la signature du fondateur'));
            img.src = '/signature/oligui.png';
        });
    };

    // ==========================================
    // FONCTION TÉLÉCHARGEMENT CARTE AVEC TRACKING
    // ==========================================
    const downloadMemberCard = async () => {
        if (!memberData) return;

        setIsDownloadingCard(true);
        setDownloadError(null);

        try {
            GTMTrackingService.trackMemberCardDownload(memberData.member_number, 'image');

            const cardElement = document.getElementById(`member-card-${memberData.member_number}`);
            if (!cardElement) {
                throw new Error('Carte non trouvée');
            }

            const originalStyle = cardElement.style.cssText;
            cardElement.style.background = 'linear-gradient(135deg, rgb(37, 99, 235) 0%, rgb(22, 163, 74) 100%)';
            cardElement.style.backgroundImage = 'linear-gradient(135deg, rgb(37, 99, 235) 0%, rgb(22, 163, 74) 100%)';

            await new Promise(resolve => setTimeout(resolve, 300));

            const canvas = await html2canvas(cardElement, {
                backgroundColor: '#2563eb',
                scale: window.innerWidth < 768 ? 3 : 2,
                useCORS: true,
                allowTaint: true,
                logging: false,
                width: cardElement.offsetWidth,
                height: cardElement.offsetHeight
            });

            cardElement.style.cssText = originalStyle;

            canvas.toBlob((blob) => {
                if (blob) {
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `Carte_UDB_${memberData.member_number}.png`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                    console.log('✅ Téléchargement réussi !');

                    GTMTrackingService.trackClick('download_success', 'member_card');
                }
            }, 'image/png', 1.0);

        } catch (error) {
            console.error('Erreur:', error);
            setDownloadError('Erreur de téléchargement. Essayez une capture d\'écran manuelle.');

            GTMTrackingService.trackTechnicalError(
                'card_download_error',
                (error as Error).message,
                'member_card'
            );
        } finally {
            setIsDownloadingCard(false);
        }
    };

    // ==========================================
    // FONCTION GÉNÉRATION PDF AVEC NOUVEAU FORMAT
    // ==========================================
    const generateAdhesionFormPDF = async (selfieData?: string | null) => {
        if (!memberData) return;

        setIsDownloadingPDF(true);
        setPdfError(null);

        try {
            // ← TRACKING DÉBUT GÉNÉRATION PDF
            GTMTrackingService.trackAdhesionPDFGeneration(memberData.member_number, true);

            console.log('🔄 Début génération PDF...');
            console.log('📸 Données photo pour PDF:');
            console.log('   - selfieData (paramètre):', selfieData);
            console.log('   - memberData.selfie_url:', memberData.selfie_url);
            console.log('   - Type selfieData:', typeof selfieData);
            console.log('   - Type memberData.selfie_url:', typeof memberData.selfie_url);

            const logoExists = await checkLogoAvailability();
            console.log('📋 Ressources disponibles:', {
                logo: logoExists,
                memberSignature: !!memberData.signature_data
            });

            const doc = new jsPDF('p', 'mm', 'a4');
            const pageWidth = doc.internal.pageSize.getWidth();

            const margin = 15;
            const contentWidth = pageWidth - (margin * 2);
            let currentY = margin;

            // === ENTÊTE AVEC LOGO ===
            if (logoExists) {
                try {
                    console.log('📸 Chargement du logo...');
                    const logoData = await loadLogoImage();
                    doc.addImage(logoData, 'PNG', margin, currentY, 25, 25);
                    console.log('✅ Logo ajouté avec succès');
                } catch (error) {
                    console.warn('⚠️ Erreur chargement logo, utilisation du fallback:', error);
                    doc.setFillColor(37, 99, 235);
                    doc.roundedRect(margin, currentY, 25, 25, 3, 3, 'F');
                    doc.setTextColor(255, 255, 255);
                    doc.setFontSize(10);
                    doc.text('UDB', margin + 8, currentY + 15);
                }
            } else {
                console.log('📦 Utilisation du fallback logo');
                doc.setFillColor(37, 99, 235);
                doc.roundedRect(margin, currentY, 25, 25, 3, 3, 'F');
                doc.setTextColor(255, 255, 255);
                doc.setFontSize(10);
                doc.text('UDB', margin + 8, currentY + 15);
            }

            // Photo à droite - Version améliorée avec debugging renforcé
            console.log('🔍 Sources photo disponibles:');
            console.log('   1. selfieData:', selfieData, 'Type:', typeof selfieData);
            console.log('   2. memberData.selfie_url:', memberData.selfie_url, 'Type:', typeof memberData.selfie_url);
            
            // Priorité 1: selfieData passé en paramètre
            // Priorité 2: memberData.selfie_url 
            let photoUrl = null;
            if (selfieData && typeof selfieData === 'string' && selfieData.trim() !== '') {
                photoUrl = selfieData;
                console.log('✅ Photo sélectionnée: selfieData');
            } else if (memberData.selfie_url && typeof memberData.selfie_url === 'string' && memberData.selfie_url.trim() !== '') {
                photoUrl = memberData.selfie_url;
                console.log('✅ Photo sélectionnée: memberData.selfie_url');
            } else {
                console.log('❌ Aucune photo valide trouvée');
            }
            
            console.log('🔍 Photo URL finale:', photoUrl);
            
            // Essayer aussi de récupérer depuis l'image affichée dans la carte
            let cardPhotoUrl = null;
            try {
                const memberCardElement = document.querySelector(`#member-card-${memberData.member_number} img`);
                if (memberCardElement) {
                    cardPhotoUrl = (memberCardElement as HTMLImageElement).src;
                    console.log('📷 Photo trouvée dans la carte affichée:', cardPhotoUrl);
                }
            } catch (error) {
                console.log('⚠️ Impossible de récupérer la photo depuis la carte:', error);
            }

            const finalPhotoUrl = photoUrl || cardPhotoUrl;
            console.log('📍 URL photo finale sélectionnée:', finalPhotoUrl, 'Type:', typeof finalPhotoUrl);
            
            // Vérifier que nous avons une string valide
            const isValidPhotoUrl = finalPhotoUrl && typeof finalPhotoUrl === 'string' && finalPhotoUrl.trim().length > 0;
            console.log('✅ Photo URL valide?', isValidPhotoUrl);
            
            if (isValidPhotoUrl) {
                try {
                    console.log('📸 Traitement de la photo...', finalPhotoUrl);
                    console.log('📋 Type de photo URL:', typeof finalPhotoUrl, 'Longueur:', finalPhotoUrl.length);
                    const processImage = (): Promise<void> => {
                        return new Promise((resolve, reject) => {
                            // Vérifier et convertir en string si nécessaire
                            const photoUrlString = String(finalPhotoUrl || '');
                            console.log('🔧 Conversion en string:', typeof finalPhotoUrl, '→', typeof photoUrlString, photoUrlString);
                            
                            // Déterminer l'URL finale
                            let finalUrl: string = photoUrlString;
                            if (photoUrlString.startsWith('/') || photoUrlString.startsWith('./')) {
                                finalUrl = `${window.location.origin}${photoUrlString}`;
                                console.log('🔄 URL relative détectée, conversion:', photoUrlString, '→', finalUrl);
                            }
                            console.log('📍 URL finale pour chargement:', finalUrl);

                            // Première tentative avec CORS
                            const tryWithCors = (url: string) => {
                                console.log('🚀 Tentative avec CORS pour:', url);
                                const img = new Image();
                                img.crossOrigin = 'anonymous';

                                img.onload = () => {
                                    console.log('✅ Image chargée avec succès (CORS)');
                                    console.log('📐 Dimensions originales:', img.width, 'x', img.height);
                                    try {
                                        const canvas = document.createElement('canvas');
                                        const ctx = canvas.getContext('2d');
                                        
                                        const canvasSize = 200;
                                        canvas.width = canvasSize;
                                        canvas.height = canvasSize;

                                        if (ctx) {
                                            // Calculer les dimensions pour respecter le ratio
                                            const imgRatio = img.width / img.height;
                                            let drawWidth, drawHeight, drawX, drawY;
                                            
                                            if (imgRatio > 1) {
                                                // Image plus large que haute
                                                drawHeight = canvasSize * 0.9; // 90% du canvas pour laisser un peu de marge
                                                drawWidth = drawHeight * imgRatio;
                                                drawX = (canvasSize - drawWidth) / 2;
                                                drawY = (canvasSize - drawHeight) / 2;
                                            } else {
                                                // Image plus haute que large ou carrée
                                                drawWidth = canvasSize * 0.9;
                                                drawHeight = drawWidth / imgRatio;
                                                drawX = (canvasSize - drawWidth) / 2;
                                                drawY = (canvasSize - drawHeight) / 2;
                                            }
                                            
                                            console.log('📐 Dimensions calculées:', {
                                                original: `${img.width}x${img.height}`,
                                                ratio: imgRatio.toFixed(2),
                                                draw: `${drawWidth.toFixed(1)}x${drawHeight.toFixed(1)}`,
                                                position: `${drawX.toFixed(1)},${drawY.toFixed(1)}`
                                            });

                                            // Dessiner un cercle blanc comme arrière-plan
                                            ctx.fillStyle = 'white';
                                            ctx.fillRect(0, 0, canvasSize, canvasSize);
                                            
                                            // Créer un masque circulaire
                                            ctx.save();
                                            ctx.beginPath();
                                            ctx.arc(canvasSize/2, canvasSize/2, (canvasSize/2) - 5, 0, Math.PI * 2);
                                            ctx.clip();
                                            
                                            // Dessiner l'image centrée avec ratio respecté
                                            ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
                                            ctx.restore();
                                            
                                            const imageData = canvas.toDataURL('image/jpeg', 0.9);
                                            doc.addImage(imageData, 'JPEG', pageWidth - margin - 25, currentY, 25, 25);
                                            resolve();
                                        } else {
                                            tryWithoutCors(url);
                                        }
                                    } catch (error) {
                                        console.warn('⚠️ Erreur avec CORS, tentative sans CORS');
                                        tryWithoutCors(url);
                                    }
                                };

                                img.onerror = (error) => {
                                    console.warn('❌ Échec chargement avec CORS:', error);
                                    console.warn('🔄 Tentative sans CORS...');
                                    tryWithoutCors(url);
                                };
                                
                                img.src = url;
                            };

                            // Deuxième tentative sans CORS
                            const tryWithoutCors = (url: string) => {
                                console.log('🔄 Tentative sans CORS pour:', url);
                                const img = new Image();
                                // Pas de crossOrigin

                                img.onload = () => {
                                    console.log('✅ Image chargée avec succès (sans CORS)');
                                    console.log('📐 Dimensions originales:', img.width, 'x', img.height);
                                    try {
                                        const canvas = document.createElement('canvas');
                                        const ctx = canvas.getContext('2d');
                                        
                                        const canvasSize = 200;
                                        canvas.width = canvasSize;
                                        canvas.height = canvasSize;

                                        if (ctx) {
                                            // Calculer les dimensions pour respecter le ratio
                                            const imgRatio = img.width / img.height;
                                            let drawWidth, drawHeight, drawX, drawY;
                                            
                                            if (imgRatio > 1) {
                                                // Image plus large que haute
                                                drawHeight = canvasSize * 0.9;
                                                drawWidth = drawHeight * imgRatio;
                                                drawX = (canvasSize - drawWidth) / 2;
                                                drawY = (canvasSize - drawHeight) / 2;
                                            } else {
                                                // Image plus haute que large ou carrée
                                                drawWidth = canvasSize * 0.9;
                                                drawHeight = drawWidth / imgRatio;
                                                drawX = (canvasSize - drawWidth) / 2;
                                                drawY = (canvasSize - drawHeight) / 2;
                                            }
                                            
                                            console.log('📐 Dimensions calculées (sans CORS):', {
                                                original: `${img.width}x${img.height}`,
                                                ratio: imgRatio.toFixed(2),
                                                draw: `${drawWidth.toFixed(1)}x${drawHeight.toFixed(1)}`,
                                                position: `${drawX.toFixed(1)},${drawY.toFixed(1)}`
                                            });

                                            // Dessiner un cercle blanc comme arrière-plan
                                            ctx.fillStyle = 'white';
                                            ctx.fillRect(0, 0, canvasSize, canvasSize);
                                            
                                            // Créer un masque circulaire
                                            ctx.save();
                                            ctx.beginPath();
                                            ctx.arc(canvasSize/2, canvasSize/2, (canvasSize/2) - 5, 0, Math.PI * 2);
                                            ctx.clip();
                                            
                                            // Dessiner l'image centrée avec ratio respecté
                                            ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
                                            ctx.restore();
                                            
                                            const imageData = canvas.toDataURL('image/jpeg', 0.9);
                                            doc.addImage(imageData, 'JPEG', pageWidth - margin - 25, currentY, 25, 25);
                                            resolve();
                                        } else {
                                            reject(new Error('Contexte canvas indisponible'));
                                        }
                                    } catch (error) {
                                        reject(error);
                                    }
                                };

                                img.onerror = (error) => {
                                    console.error('❌ Échec définitif chargement photo:', url, error);
                                    console.error('💥 Tous les essais ont échoué');
                                    reject(new Error(`Impossible de charger: ${url}`));
                                };
                                
                                img.src = url;
                            };

                            // Gérer les différents types d'URLs
                            if (finalUrl.startsWith('blob:') || finalUrl.startsWith('data:')) {
                                tryWithoutCors(finalUrl); // Les blobs n'ont pas besoin de CORS
                            } else {
                                tryWithCors(finalUrl);
                            }
                        });
                    };

                    await processImage();
                    console.log('✅ Photo ajoutée avec succès au PDF');
                } catch (error) {
                    console.error('💥 ERREUR CHARGEMENT PHOTO:', error);
                    
                    // Dernière tentative: capturer depuis la carte affichée avec html2canvas
                    try {
                        console.log('🔄 Tentative de capture depuis la carte affichée...');
                        const memberCardElement = document.querySelector(`#member-card-${memberData.member_number} img`) as HTMLImageElement;
                        
                        if (memberCardElement && memberCardElement.complete) {
                            const canvas = document.createElement('canvas');
                            const ctx = canvas.getContext('2d');
                            const canvasSize = 200;
                            canvas.width = canvasSize;
                            canvas.height = canvasSize;
                            
                            if (ctx) {
                                // Calculer les dimensions pour respecter le ratio depuis la carte
                                const imgRatio = memberCardElement.naturalWidth / memberCardElement.naturalHeight;
                                let drawWidth, drawHeight, drawX, drawY;
                                
                                if (imgRatio > 1) {
                                    drawHeight = canvasSize * 0.9;
                                    drawWidth = drawHeight * imgRatio;
                                    drawX = (canvasSize - drawWidth) / 2;
                                    drawY = (canvasSize - drawHeight) / 2;
                                } else {
                                    drawWidth = canvasSize * 0.9;
                                    drawHeight = drawWidth / imgRatio;
                                    drawX = (canvasSize - drawWidth) / 2;
                                    drawY = (canvasSize - drawHeight) / 2;
                                }
                                
                                console.log('📐 Capture carte - Dimensions:', {
                                    natural: `${memberCardElement.naturalWidth}x${memberCardElement.naturalHeight}`,
                                    ratio: imgRatio.toFixed(2),
                                    draw: `${drawWidth.toFixed(1)}x${drawHeight.toFixed(1)}`
                                });

                                ctx.fillStyle = 'white';
                                ctx.fillRect(0, 0, canvasSize, canvasSize);
                                ctx.save();
                                ctx.beginPath();
                                ctx.arc(canvasSize/2, canvasSize/2, (canvasSize/2) - 5, 0, Math.PI * 2);
                                ctx.clip();
                                ctx.drawImage(memberCardElement, drawX, drawY, drawWidth, drawHeight);
                                ctx.restore();
                                
                                const imageData = canvas.toDataURL('image/jpeg', 0.9);
                                doc.addImage(imageData, 'JPEG', pageWidth - margin - 25, currentY, 25, 25);
                                console.log('✅ Photo capturée depuis la carte avec ratio respecté!');
                                return; // Sortir sans afficher le placeholder
                            }
                        }
                    } catch (captureError) {
                        console.warn('⚠️ Échec capture depuis carte:', captureError);
                    }
                    
                    console.warn('📝 Utilisation du placeholder "NON DISPONIBLE"');
                    doc.setFillColor(200, 200, 200);
                    doc.roundedRect(pageWidth - margin - 25, currentY, 25, 25, 3, 3, 'F');
                    doc.setTextColor(100, 100, 100);
                    doc.setFontSize(8);
                    doc.text('PHOTO', pageWidth - margin - 18, currentY + 15);
                    doc.setFontSize(6);
                    doc.text('NON DISPONIBLE', pageWidth - margin - 22, currentY + 20);
                }
            } else {
                console.log('❌ AUCUNE PHOTO TROUVÉE');
                console.log('   - selfieData:', selfieData);
                console.log('   - memberData.selfie_url:', memberData.selfie_url);
                console.log('   - cardPhotoUrl:', cardPhotoUrl);
                console.log('📝 Utilisation du placeholder par défaut');
                doc.setFillColor(200, 200, 200);
                doc.roundedRect(pageWidth - margin - 25, currentY, 25, 25, 3, 3, 'F');
                doc.setTextColor(100, 100, 100);
                doc.setFontSize(8);
                doc.text('PHOTO', pageWidth - margin - 18, currentY + 15);
            }

            // Titre organisation
            doc.setTextColor(0, 0, 0);
            doc.setFontSize(16);
            doc.setFont('helvetica', 'bold');
            const orgName = 'UNION DEMOCRATIQUE DES BATISSEURS';
            const orgNameWidth = doc.getTextWidth(orgName);
            doc.text(orgName, (pageWidth - orgNameWidth) / 2, currentY + 10);

            doc.setFontSize(14);
            const title = 'FICHE D\'ADHESION';
            const titleWidth = doc.getTextWidth(title);
            doc.text(title, (pageWidth - titleWidth) / 2, currentY + 20);

            currentY += 30;

            // === QR CODE ===
            try {
                console.log('📱 Génération du QR Code...');
                const qrUrl = generatePlainTextMemberQR('400x400');

                if (!qrUrl) {
                    throw new Error('Impossible de générer le QR Code');
                }

                const qrSize = 40;
                const qrX = (pageWidth - qrSize) / 2;

                doc.addImage(qrUrl, 'PNG', qrX, currentY, qrSize, qrSize);

                doc.setFontSize(8);
                doc.setFont('helvetica', 'italic');
                doc.setTextColor(100, 100, 100);
                const qrLabel = 'QR Code membre - Scannez pour vérifier l\'authenticité';
                const qrLabelWidth = doc.getTextWidth(qrLabel);
                doc.text(qrLabel, (pageWidth - qrLabelWidth) / 2, currentY + qrSize + 5);

                console.log('✅ QR Code ajouté avec succès');
                currentY += qrSize + 8;
            } catch (error) {
                console.warn('⚠️ Erreur ajout QR Code:', error);
                currentY += 10;
            }

            // === NOUVEAU FORMULAIRE EN 4 SECTIONS ===

            // Fonction helper pour créer les sections avec en-tête bleu
            const createSection = (title: string, yPos: number): number => {
                // En-tête vert de la section (couleur #009126)
                doc.setFillColor(0, 145, 38); // #009126 en RGB
                doc.rect(margin, yPos, contentWidth, 6, 'F');

                // Texte blanc sur fond vert (centré verticalement)
                doc.setTextColor(255, 255, 255);
                doc.setFontSize(10); // Réduit de 11 à 10
                doc.setFont('helvetica', 'bold');
                doc.text(title, margin + 3, yPos + 4.2); // Ajusté pour centrage vertical (6/2 + offset police)

                return yPos + 6; // Ajusté selon la nouvelle hauteur
            };

            // Fonction helper pour ajouter des champs
            const addField = (label: string, value: string, x: number, y: number, width: number = 80): number => {
                doc.setTextColor(0, 0, 0);
                doc.setFontSize(10);
                doc.setFont('helvetica', 'bold');
                doc.text(`${label} :`, x, y);

                doc.setFont('helvetica', 'normal');
                // Nettoyer et limiter la valeur si trop longue
                let cleanValue = value || '';
                const maxWidth = width - 2;
                if (doc.getTextWidth(cleanValue) > maxWidth) {
                    while (doc.getTextWidth(cleanValue + '...') > maxWidth && cleanValue.length > 0) {
                        cleanValue = cleanValue.slice(0, -1);
                    }
                    cleanValue += '...';
                }
                doc.text(cleanValue, x + 35, y);

                return y + 7;
            };

            // === 1. SECTION J'ADHÈRE ===
            currentY = createSection('1. J\'ADHÈRE', currentY);
            currentY += 5;

            // Première ligne (Nom / Prénom)
            let nextY = addField('Nom', memberData.last_name, margin + 5, currentY, 80);
            addField('Prénom', memberData.first_name, margin + 95, currentY, 80);
            currentY = nextY;

            // Deuxième ligne (Date naissance / Lieu naissance)
            if (memberData.date_of_birth) {
                nextY = addField('Date de naissance', new Date(memberData.date_of_birth).toLocaleDateString('fr-FR'), margin + 5, currentY, 80);
            } else {
                nextY = addField('Date de naissance', '', margin + 5, currentY, 80);
            }
            addField('Lieu de naissance', memberData.place_of_birth || '', margin + 95, currentY, 80);
            currentY = nextY;

            // Troisième ligne (Profession / Qualité)
            nextY = addField('Profession', memberData.profession || '', margin + 5, currentY, 80);
            addField('Qualité', 'MEMBRE', margin + 95, currentY, 80);
            currentY = nextY;

            // Quatrième ligne (NIP / N° Membre)
            nextY = addField('NIP', memberData.nip || '', margin + 5, currentY, 80);
            addField('N° Membre', memberData.member_number || '', margin + 95, currentY, 80);
            currentY = nextY + 0;

            // === 2. SECTION ADRESSE ===
            currentY = createSection('2. ADRESSE', currentY);
            currentY += 5;

            // Première ligne (Pays / Province)
            nextY = addField('Pays', memberData.country || 'Gabon', margin + 5, currentY, 80);
            addField('Province', memberData.province, margin + 95, currentY, 80);
            currentY = nextY;

            // Deuxième ligne (Département / Ville)
            nextY = addField('Département', memberData.department || '', margin + 5, currentY, 80);
            addField('Ville', memberData.city, margin + 95, currentY, 80);
            currentY = nextY;

            // === 3. SECTION CONTACTS ===
            currentY = createSection('3. CONTACTS', currentY);
            currentY += 5;

            nextY = addField('WhatsApp', memberData.phone, margin + 5, currentY, 160);
            currentY = nextY;
            nextY = addField('Email', memberData.email || '', margin + 5, currentY, 160);
            currentY = nextY + 0;

            // === 4. SECTION ENGAGEMENT SUR L'HONNEUR ===
            currentY = createSection('4. ENGAGEMENT SUR L\'HONNEUR', currentY);
            currentY += 5;

            // Case cochée à gauche - ALIGNEMENT CORRIGÉ
            const checkboxSize = 3;
            const checkboxX = margin + 5;
            const checkboxY = currentY - 2; // Corrigé de -1 à -2 pour meilleur alignement

            // Dessiner la case
            doc.setDrawColor(0, 0, 0);
            doc.setLineWidth(0.2);
            doc.rect(checkboxX, checkboxY, checkboxSize, checkboxSize);

            // Dessiner la coche (✓)
            doc.setLineWidth(0.3);
            doc.line(checkboxX + 0.5, checkboxY + 1.5, checkboxX + 1.3, checkboxY + 2.3);
            doc.line(checkboxX + 1.3, checkboxY + 2.3, checkboxX + 2.5, checkboxY + 0.7);

            // Texte d'engagement à droite de la case
            doc.setTextColor(0, 0, 0);
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            const engagementText = 'J\'adhère au parti politique UDB et je m\'engage à œuvrer à son rayonnement pour bâtir le Gabon.';

            // Découper le texte en lignes (ajusté pour la case cochée)
            const words = engagementText.split(' ');
            let line = '';
            const lines = [];
            const maxLineWidth = contentWidth - 15; // Espace pour la case cochée

            for (const word of words) {
                const testLine = line + word + ' ';
                if (doc.getTextWidth(testLine) > maxLineWidth && line !== '') {
                    lines.push(line.trim());
                    line = word + ' ';
                } else {
                    line = testLine;
                }
            }
            lines.push(line.trim());

            lines.forEach((textLine, index) => {
                doc.text(textLine, checkboxX + checkboxSize + 3, currentY + (index * 5));
            });

            currentY += lines.length * 5 + 10;

            // === SIGNATURES (SECTION EXISTANTE) ===
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(0, 0, 0);

            const signaturesY = currentY;
            const leftColumnX = margin;
            const rightColumnX = pageWidth - margin - 80;
            const signatureWidth = 80;
            const signatureHeight = 40;

            // === COLONNE GAUCHE : SIGNATURE ADHÉRENT ===
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.text('Adhérent', leftColumnX + (signatureWidth / 2), signaturesY, { align: 'center' });

            // Signature adhérent
            if (memberData.signature_data) {
                try {
                    console.log('✍️ Ajout de la signature du membre...');
                    doc.addImage(
                        memberData.signature_data,
                        'PNG',
                        leftColumnX + 2,
                        signaturesY + 7,
                        signatureWidth - 4,
                        signatureHeight - 4
                    );
                    console.log('✅ Signature membre ajoutée');
                } catch (error) {
                    console.warn('⚠️ Erreur signature membre:', error);
                }
            }

            // Nom adhérent sous la signature
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.text(`${memberData.first_name} ${memberData.last_name}`, leftColumnX + (signatureWidth / 2), signaturesY + signatureHeight + 15, { align: 'center' });

            // === COLONNE DROITE : SIGNATURE FONDATEUR ===
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.text('Président fondateur, Président du parti', rightColumnX + (signatureWidth / 2), signaturesY, { align: 'center' });

            // Charger et ajouter la signature fondateur
            try {
                console.log('✍️ Chargement de la signature du fondateur...');
                const founderSignature = await loadFounderSignature();

                doc.addImage(
                    founderSignature,
                    'PNG',
                    rightColumnX + 2,
                    signaturesY + 7,
                    signatureWidth - 4,
                    signatureHeight - 4
                );
                console.log('✅ Signature fondateur ajoutée');
            } catch (error) {
                console.warn('⚠️ Erreur signature fondateur:', error);
            }

            // Nom fondateur sous la signature
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.text('Brice Clotaire Oligui Nguema', rightColumnX + (signatureWidth / 2), signaturesY + signatureHeight + 15, { align: 'center' });

            currentY = signaturesY + signatureHeight + 25;

            // === POLITIQUE DE PROTECTION DES DONNÉES ===
            currentY += 5;
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(80, 80, 80);

            const privacyText = 'POLITIQUE DE PROTECTION DES DONNÉES PERSONNELLES : Les informations que vous nous communiquez sont exclusivement réservées à l\'usage du parti « Union Démocratique des Bâtisseurs ». Vous consentez à ce qu\'elles soient utilisées pour les communications internes au parti. Vous bénéficiez d\'un droit d\'accès, de rectification, de suppression, d\'opposition, de limitation et de portabilité des informations vous concernant. Pour exercer vos droits, adressez-vous à contact@udb.ga';

            // Découper le texte de confidentialité en lignes avec justification
            const privacyWords = privacyText.split(' ');
            let privacyLine = '';
            const privacyLines = [];
            const maxPrivacyLineWidth = contentWidth;

            for (const word of privacyWords) {
                const testLine = privacyLine + word + ' ';
                if (doc.getTextWidth(testLine) > maxPrivacyLineWidth && privacyLine !== '') {
                    privacyLines.push(privacyLine.trim());
                    privacyLine = word + ' ';
                } else {
                    privacyLine = testLine;
                }
            }
            privacyLines.push(privacyLine.trim());

            // Afficher les lignes avec justification (sauf la dernière)
            privacyLines.forEach((textLine, index) => {
                if (index === privacyLines.length - 1) {
                    // Dernière ligne : alignement normal à gauche
                    doc.text(textLine, margin, currentY + (index * 4));
                } else {
                    // Autres lignes : justification
                    const words = textLine.split(' ');
                    if (words.length > 1) {
                        const totalTextWidth = words.reduce((sum, word) => sum + doc.getTextWidth(word), 0);
                        const totalSpaceWidth = maxPrivacyLineWidth - totalTextWidth;
                        const spaceWidth = totalSpaceWidth / (words.length - 1);

                        let currentX = margin;
                        words.forEach((word, wordIndex) => {
                            doc.text(word, currentX, currentY + (index * 4));
                            if (wordIndex < words.length - 1) {
                                currentX += doc.getTextWidth(word) + spaceWidth;
                            }
                        });
                    } else {
                        doc.text(textLine, margin, currentY + (index * 4));
                    }
                }
            });

            currentY += privacyLines.length * 4 + 5;

            const fileName = `Fiche_Adhesion_UDB_${memberData.first_name}_${memberData.last_name}_${new Date().toISOString().split('T')[0]}.pdf`;
            doc.save(fileName);

            console.log('✅ PDF avec nouveau format généré avec succès !');

            // ← TRACKING SUCCÈS PDF
            GTMTrackingService.trackClick('pdf_download_success', 'member_card');

        } catch (error) {
            console.error('❌ Erreur génération PDF:', error);
            setPdfError('Erreur lors de la génération de la fiche d\'adhésion. Veuillez réessayer.');

            // ← TRACKING ERREUR PDF
            GTMTrackingService.trackAdhesionPDFGeneration(memberData.member_number, false);
            GTMTrackingService.trackTechnicalError(
                'pdf_generation_error',
                (error as Error).message,
                'member_card'
            );
        } finally {
            setIsDownloadingPDF(false);
        }
    };
    // ==========================================
    // FONCTIONS DE PARTAGE AVEC TRACKING
    // ==========================================
    const generateShareLinks = () => {
        if (!memberData) return null;

        const baseUrl = window.location.origin;
        const referralLink = `${baseUrl}/adhesion?ref=${memberData.referral_code}`;

        const facebookMessage = `🎉 Rejoignez l'Union Démocratique des Bâtisseurs !

Je vous invite à rejoindre notre mouvement politique pour transformer le Gabon.

✅ Bénéficiez de mon parrainage
✅ Adhésion simplifiée  
✅ Accompagnement personnalisé

🔗 Utilisez mon code de parrainage: ${memberData.referral_code}

👉 ${referralLink}

#UDB #UnionDesBatisseurs #Gabon #Politique`;

        const whatsappMessage = `🎉 Rejoignez l'Union Démocratique des Bâtisseurs !

Salut ! Je t'invite à rejoindre l'UDB, le mouvement qui transforme le Gabon.

Avec mon code de parrainage *${memberData.referral_code}*, tu bénéficies d'un accompagnement personnalisé !

👉 ${referralLink}

#UDB #UnionDesBatisseurs`;

        return {
            referralLink,
            facebookMessage,
            whatsappMessage,
            whatsapp: `https://wa.me/?text=${encodeURIComponent(whatsappMessage)}`,
            facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}`,
            telegram: `https://t.me/share/url?url=${encodeURIComponent(referralLink)}&text=${encodeURIComponent(facebookMessage)}`,
            twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(`🎉 Rejoignez l'UDB avec mon code ${memberData.referral_code}!\n\n👉 ${referralLink}\n\n#UDB #UnionDesBatisseurs #Gabon`)}`,
            linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralLink)}&summary=${encodeURIComponent('Rejoignez l\'Union Démocratique des Bâtisseurs')}`
        };
    };

    const copyReferralLink = () => {
        const shareLinks = generateShareLinks();
        if (!shareLinks || !memberData) return;

        navigator.clipboard.writeText(shareLinks.referralLink);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);

        GTMTrackingService.trackReferralLinkCopy(memberData.member_number, memberData.referral_code);
    };

    const shareWhatsApp = () => {
        const shareLinks = generateShareLinks();
        if (!shareLinks || !memberData) return;

        window.open(shareLinks.whatsapp, '_blank');

        GTMTrackingService.trackSocialShare('whatsapp', memberData.member_number, memberData.referral_code);
    };

    const shareToSocial = (platform: 'telegram' | 'twitter' | 'facebook' | 'linkedin') => {
        const shareLinks = generateShareLinks();
        if (!shareLinks || !memberData) return;

        window.open(shareLinks[platform], '_blank');

        GTMTrackingService.trackSocialShare(platform, memberData.member_number, memberData.referral_code);
    };

    // ==========================================
    // FONCTIONS UTILITAIRES AVEC TRACKING
    // ==========================================
    const clearDownloadError = () => setDownloadError(null);
    const clearPdfError = () => setPdfError(null);

    const openQRModal = () => {
        setShowQRModal(true);
        if (memberData) {
            GTMTrackingService.trackQRCodeView(memberData.member_number);
        }
    };

    const closeQRModal = () => setShowQRModal(false);

    const downloadQRCode = () => {
        if (!qrCodeUrl || !memberData) return;

        const link = document.createElement('a');
        link.href = qrCodeUrl.replace('300x300', '400x400');
        link.download = `QR_${memberData.member_number}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        GTMTrackingService.trackQRCodeDownload(memberData.member_number);
    };

    // ==========================================
    // RETOUR DU HOOK
    // ==========================================
    return {
        // États
        qrCodeUrl,
        showQRModal,
        qrType,
        isDownloadingCard,
        isDownloadingPDF,
        downloadError,
        pdfError,
        copySuccess,

        // Fonctions principales
        downloadMemberCard,
        generateAdhesionFormPDF,
        copyReferralLink,
        shareWhatsApp,
        shareToSocial,

        // Fonctions QR Code
        openQRModal,
        closeQRModal,
        downloadQRCode,

        // Fonctions utilitaires
        clearDownloadError,
        clearPdfError,
        generateShareLinks,

        // Setters pour état (si besoin)
        setQrType,
        setShowQRModal
    };
};
